<template>
  <div class="clinics-map-container">
    
    <div :id="mapContainerId" class="map-container" style="width: 100%; height: 800px;"></div>
    
    <v-overlay :value="loading" z-index="9999">
      <div class="mt-4">{{ loadingMessage }}</div>
    </v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import Clinic from "../../classes/clinic";

export default {
  name: "ClinicsMap",
  props: {
    current_user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      map: null,
      clinics: [],
      markers: [],
      popups: [],
      loading: false,
      loadingMessage: "Loading clinics...",
      mapboxLoaded: false,
      mapboxLoadPromise: null,
      geocoderLoaded: false,
      geocoderLoadPromise: null,
      mapContainerId: `clinics-map-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
  },
  computed: {
    mapBoxAccessToken() {
      return "pk.eyJ1IjoicGhheWVzIiwiYSI6ImNta3ZqOXp6cjA4ZWMzZHB4NnBwZ3lsZzUifQ.B_0xJ0ET0boSRhXQiZnaFw";
    },
  },
  mounted() {
    this.loadMapboxScripts();
  },
  beforeDestroy() {
    // Clean up map and markers
    if (this.map) {
      this.markers.forEach(marker => marker.remove());
      this.popups.forEach(popup => popup.remove());
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    loadMapboxScripts() {
      // Check if already loaded
      if (window.mapboxgl) {
        this.mapboxLoaded = true;
        this.initializeMap();
        return;
      }

      // Check if script is already in the DOM
      const existingScript = document.querySelector(
        'script[src="https://api.mapbox.com/mapbox-gl-js/v3.18.1/mapbox-gl.js"]'
      );
      if (existingScript && window.mapboxgl) {
        this.mapboxLoaded = true;
        this.initializeMap();
        return;
      }

      // Return existing promise if script is already loading
      if (this.mapboxLoadPromise) {
        this.mapboxLoadPromise.then(() => {
          this.initializeMap();
        });
        return;
      }

      // Load CSS (idempotent - check if already exists)
      const existingLink = document.querySelector(
        'link[href="https://api.mapbox.com/mapbox-gl-js/v3.18.1/mapbox-gl.css"]'
      );
      if (!existingLink) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://api.mapbox.com/mapbox-gl-js/v3.18.1/mapbox-gl.css";
        document.head.appendChild(link);
      }

      // Load JS
      this.mapboxLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://api.mapbox.com/mapbox-gl-js/v3.18.1/mapbox-gl.js";
        script.async = true;
        
        script.onload = () => {
          this.mapboxLoaded = true;
          this.mapboxLoadPromise = null;
          resolve();
        };
        
        script.onerror = () => {
          this.mapboxLoadPromise = null;
          console.error("Failed to load Mapbox GL JS");
          this.loadingMessage = "Failed to load map library";
          reject(new Error("Failed to load Mapbox GL JS"));
        };
        
        document.head.appendChild(script);
      });

      this.mapboxLoadPromise.then(() => {
        this.loadGeocoderScripts();
      }).catch(() => {
        this.loading = false;
      });
    },
    
    loadGeocoderScripts() {
      // Check if already loaded
      if (window.MapboxGeocoder) {
        this.geocoderLoaded = true;
        this.initializeMap();
        return;
      }

      // Check if script is already in the DOM
      const existingScript = document.querySelector(
        'script[src*="mapbox-gl-geocoder"]'
      );
      if (existingScript && window.MapboxGeocoder) {
        this.geocoderLoaded = true;
        this.initializeMap();
        return;
      }

      // Return existing promise if script is already loading
      if (this.geocoderLoadPromise) {
        this.geocoderLoadPromise.then(() => {
          this.initializeMap();
        });
        return;
      }

      // Load CSS (idempotent - check if already exists)
      const existingGeocoderLink = document.querySelector(
        'link[href*="mapbox-gl-geocoder"]'
      );
      if (!existingGeocoderLink) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v5.0.0/mapbox-gl-geocoder.css";
        document.head.appendChild(link);
      }

      // Load JS
      this.geocoderLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-geocoder/v5.0.0/mapbox-gl-geocoder.min.js";
        script.async = true;
        
        script.onload = () => {
          this.geocoderLoaded = true;
          this.geocoderLoadPromise = null;
          resolve();
        };
        
        script.onerror = () => {
          this.geocoderLoadPromise = null;
          console.error("Failed to load Mapbox GL Geocoder");
          // Still initialize map even if geocoder fails
          this.initializeMap();
          reject(new Error("Failed to load Mapbox GL Geocoder"));
        };
        
        document.head.appendChild(script);
      });

      this.geocoderLoadPromise.then(() => {
        this.initializeMap();
      }).catch(() => {
        // Map will still initialize even if geocoder fails
        this.initializeMap();
      });
    },
    
    async initializeMap() {
      if (!window.mapboxgl) {
        console.error("Mapbox GL JS not loaded");
        return;
      }

      // Set access token if available
      if (this.mapBoxAccessToken) {
        window.mapboxgl.accessToken = this.mapBoxAccessToken;
      }
      
      // Initialize map with streets base map
      this.map = new window.mapboxgl.Map({
        container: this.mapContainerId,
        style: 'mapbox://styles/mapbox/standard',
        projection: 'mercator',
        zoom: 2,
      });

      // Add navigation controls
      this.map.addControl(new window.mapboxgl.NavigationControl());

      // Add geocoder search box
      if (window.MapboxGeocoder) {
        const geocoder = new window.MapboxGeocoder({
          accessToken: this.mapBoxAccessToken,
          mapboxgl: window.mapboxgl,
          marker: false, // Don't add a marker, just navigate
          placeholder: "Search for a city or address...",
          proximity: {
            longitude: 0,
            latitude: 20,
          },
        });

        // Add geocoder to the map
        this.map.addControl(geocoder, "top-left");

        // Optional: Handle result event for custom behavior
        geocoder.on("result", (e) => {
          // The geocoder automatically navigates to the result
          // You can add custom logic here if needed
          const [lng, lat] = e.result.center;
          this.map.flyTo({
            center: [lng, lat],
            zoom: 12,
          });
        });
      }

      // Wait for map to load
      this.map.on("load", () => {
        this.loadClinics();
      });
    },
    
    async loadClinics() {
      this.loading = true;
      this.loadingMessage = "Loading clinics...";
      
      try {
        const response = await axios.get("/api/clinic");
        if (response.data.status === "success") {
          this.clinics = response.data.data.map((data) => Clinic.fromJson(data));
          await this.geocodeAndAddMarkers();
        } else {
          console.error("Failed to load clinics:", response.data.error_message);
          this.loadingMessage = "Failed to load clinics";
        }
      } catch (error) {
        console.error("Error loading clinics:", error);
        this.loadingMessage = "Error loading clinics";
      } finally {
        this.loading = false;
      }
    },
    
    async geocodeAndAddMarkers() {
      if (!this.map) return;

      this.loadingMessage = "Geocoding addresses...";
      const geocodedClinics = [];
      let geocodedCount = 0;

      // Geocode clinics in batches to avoid overwhelming the geocoding service
      for (const clinic of this.clinics) {
        const address = this.buildAddress(clinic);
        if (!address) {
          continue;
        }

        try {
          const coordinates = await this.geocodeAddress(address);
          if (coordinates) {
            geocodedClinics.push({
              clinic,
              coordinates,
            });
            geocodedCount++;
            this.loadingMessage = `Geocoding addresses... (${geocodedCount}/${this.clinics.length})`;
          }
        } catch (error) {
          console.warn(`Failed to geocode address for clinic ${clinic.id}:`, error);
        }
      }

      // Add markers to map
      this.addMarkers(geocodedClinics);
      this.loadingMessage = `Loaded ${geocodedCount} clinics`;
    },
    
    buildAddress(clinic) {
      const parts = [];
      
      // Use physical address if present (check if physical_address_1 trimmed is not empty)
      const usePhysical = clinic.physical_address_1 && clinic.physical_address_1.trim() !== "";
      
      if (usePhysical) {
        // Physical address
        if (clinic.physical_address_1) {
          parts.push(clinic.physical_address_1);
        }
        if (clinic.physical_locality) {
          parts.push(clinic.physical_locality);
        }
        if (clinic.physical_district) {
          parts.push(clinic.physical_district);
        }
        if (clinic.physical_postal) {
          parts.push(clinic.physical_postal);
        }
        if (clinic.billing_country) {
          parts.push(clinic.billing_country);
        }
      } else {
        // Billing address
        if (clinic.billing_address_1) {
          parts.push(clinic.billing_address_1);
        }
        if (clinic.billing_locality) {
          parts.push(clinic.billing_locality);
        }
        if (clinic.billing_district) {
          parts.push(clinic.billing_district);
        }
        if (clinic.billing_postal) {
          parts.push(clinic.billing_postal);
        }
        if (clinic.billing_country) {
          parts.push(clinic.billing_country);
        }
      }
      
      return parts.length > 0 ? parts.join(", ") : null;
    },
    
    async geocodeAddress(address) {
      // Use Mapbox Geocoding API (requires access token)
      // Note: This requires a Mapbox access token to be configured
      // You can set it via: window.mapboxgl.accessToken = 'YOUR_TOKEN'
      // Or configure it in your app config
      if (this.mapBoxAccessToken) {
        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${this.mapBoxAccessToken}&limit=1`
          );
          
          if (!response.ok) {
            throw new Error(`Geocoding API error: ${response.status}`);
          }
          
          const data = await response.json();
          if (data.features && data.features.length > 0) {
            const [lng, lat] = data.features[0].center;
            return [lng, lat];
          }
        } catch (error) {
          console.warn("Mapbox geocoding failed:", error);
        }
      } else {
        console.warn("Mapbox access token not configured. Set window.mapboxgl.accessToken or window.app_config.mapbox_access_token");
      }

      // If geocoding fails, return null
      return null;
    },
    
    addMarkers(geocodedClinics) {
      if (!this.map || !geocodedClinics.length) return;

      // Clear existing markers
      this.markers.forEach(marker => marker.remove());
      this.popups.forEach(popup => popup.remove());
      this.markers = [];
      this.popups = [];

      const bounds = new window.mapboxgl.LngLatBounds();

      geocodedClinics.forEach(({ clinic, coordinates }) => {
        const [lng, lat] = coordinates;
        bounds.extend([lng, lat]);

        // Create popup
        const popup = new window.mapboxgl.Popup({ offset: 25 })
          .setHTML(this.createPopupContent(clinic));

        // Create marker
        const marker = new window.mapboxgl.Marker({
          color: clinic.has_tag("archived") ? "#888" : "#3f51b5",
        })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(this.map);

        this.markers.push(marker);
        this.popups.push(popup);
      });

      // Fit map to show all markers
      if (geocodedClinics.length > 0) {
        this.map.fitBounds(bounds, {
          padding: 50,
        });
      }
    },
    
    createPopupContent(clinic) {
      const address = this.buildAddress(clinic);
      const status = clinic.has_tag("archived") ? "Archived" : "Active";
      
      return `
        <div style="min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">${this.escapeHtml(clinic.title)}</h3>
          ${address ? `<p style="margin: 4px 0; font-size: 12px; color: #666;">${this.escapeHtml(address)}</p>` : ""}
          ${clinic.phone ? `<p style="margin: 4px 0; font-size: 12px;">📞 ${this.escapeHtml(clinic.phone)}</p>` : ""}
          <p style="margin: 4px 0; font-size: 12px;">
            <span style="color: ${clinic.has_tag("archived") ? "#888" : "#4caf50"}; font-weight: bold;">${status}</span>
          </p>
          <a href="/app/admin/clinic/${clinic.id}" target="_blank" style="font-size: 12px; color: #1976d2; text-decoration: none;">
            View Details →
          </a>
        </div>
      `;
    },
    
    escapeHtml(text) {
      if (!text) return "";
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },
  },
};
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 800px;
  position: relative;
}

/* Mapbox GL CSS overrides if needed */
::v-deep .mapboxgl-popup-content {
  border-radius: 4px;
}

::v-deep .mapboxgl-popup-close-button {
  font-size: 20px;
  padding: 4px 8px;
}

/* Geocoder styling */
::v-deep .mapboxgl-ctrl-geocoder {
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  min-width: 300px;
}

::v-deep .mapboxgl-ctrl-geocoder input {
  font-size: 14px;
  padding: 8px 12px;
}

::v-deep .mapboxgl-ctrl-geocoder .suggestions {
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>

